import { QuizletResultsDto } from '@/api/generated/model/quizletResultsDto';
import { FAQ } from '@/app/components/faq/FAQ';
import { StickyButton } from '@/app/components/ppra/components/ppra-results-page/components';
import HowItWorks from '@/app/components/ppra/components/ppra-results-page/components/how-it-works/HowItWorks';
import animationData from '@/assets/animations/risk-graph.json';
import { HeroVideo } from '@/components/hero-video/hero-video';
import MarkdownText from '@/components/markdown/markdown-text';
import NavigationButton from '@/components/navigation/navigation-button';
import { ROUTE_PAYMENT } from '@/constants/routes';
import useStickyButton from '@/hooks/use-sticky-button';
import { Box, ButtonProps, Flex, Image, Section, Text } from '@cmpsr/components';
import Lottie from 'lottie-react';
import { DemoAndIndustryProofs } from '../demo-and-industry-proofs/demo-and-industry-proofs';
import { PersonalizedList } from '../personalized-list/personalized-list';
import { SocialProofMarquee } from '../social-proof-marquee/social-proof-marquee';

function HeroWomanHeadlineSection({ firstName }: { firstName?: string }) {
  const congratsMessage = `**Congratulations${firstName ? ` ${firstName}` : ''}!** You are a great candidate for Catch`;

  return (
    <Section height={{ base: '14rem', lg: '24.6875rem', xl: '35rem' }} position="relative" width="100%">
      <HeroVideo
        src="/videos/catch-hero.mp4"
        children={
          <Box maxWidth={{ base: '100%', lg: '768px' }}>
            <MarkdownText color="#FFF" fontSize={{ base: '1.875rem', lg: '4.54688rem' }} lineHeight={'111%'}>
              {congratsMessage}
            </MarkdownText>
          </Box>
        }
      />
    </Section>
  );
}

function HeadlineCTASection({
  mainButtonRef,
  ctaText,
  ctaButtonStyle,
}: {
  mainButtonRef: React.RefObject<HTMLButtonElement>;
  ctaText: string;
  ctaButtonStyle: ButtonProps;
}) {
  return (
    <Section
      display="flex"
      flexDirection="column"
      gap="2rem"
      alignItems="center"
      paddingX={{ base: 'spacer-2', lg: 'none' }}
      maxWidth={{ base: '100%', lg: '768px' }}
    >
      {/* TODO: GROW-632: Display this once we have question for cancer interest */}
      <MarkdownText
        fontSize={{ base: '1.875rem', lg: '4.23463rem' }}
        lineHeight="111%"
        textAlign="center"
        display="none"
        flexDirection="column"
      >
        Get your risk scores for **colon cancer and 20 other cancers**
      </MarkdownText>
      <NavigationButton to={ROUTE_PAYMENT} ref={mainButtonRef} {...ctaButtonStyle}>
        {ctaText}
      </NavigationButton>
    </Section>
  );
}

function PersonalizedCTASection({
  mainButtonRef,
  ctaText,
  ctaButtonStyle,
  results,
}: {
  mainButtonRef: React.RefObject<HTMLButtonElement>;
  ctaText: string;
  ctaButtonStyle: ButtonProps;
  results?: QuizletResultsDto;
}) {
  return (
    <Section
      display="flex"
      justifyContent="center"
      gap="2rem"
      flexDirection="column"
      alignItems="center"
      maxWidth={{ base: '90%', lg: '600px' }}
    >
      <PersonalizedList results={results} />
      <HeadlineCTASection mainButtonRef={mainButtonRef} ctaText={ctaText} ctaButtonStyle={ctaButtonStyle} />
    </Section>
  );
}

function HowItWorksSection() {
  return (
    <Section display="flex" flexDirection="column" gap="2rem" alignItems="center" width="90%">
      <HowItWorks />
    </Section>
  );
}

function LottieRiskHeadlineSection() {
  return (
    <Section
      display="flex"
      flexDirection={{ base: 'column', lg: 'row' }}
      gap="2rem"
      alignItems="center"
      paddingX={{ base: 'spacer-2', lg: 'none' }}
      maxWidth={{ base: '100%', lg: '90%' }}
    >
      <MarkdownText
        fontSize={{ base: '1.875rem', lg: '4.375rem' }}
        lineHeight="111%"
        textAlign="center"
        maxW={{ base: '100%', lg: '550px', xl: '750px' }}
      >
        Catch members can reduce their cancer risk **by up to 60%**
      </MarkdownText>
      <Box maxWidth={{ base: '700px', lg: '800px', xl: '500px' }} maxHeight={{ base: '400px', lg: '300px' }}>
        <Lottie animationData={animationData} />
      </Box>
    </Section>
  );
}

function CausesInfographicsSection() {
  return (
    <Section
      display="flex"
      flexDirection="column"
      gap="2rem"
      alignItems="center"
      paddingX={{ base: 'spacer-2', lg: 'none' }}
      maxWidth={{ base: '90%', lg: '1000px' }}
    >
      <MarkdownText fontSize={{ base: '1.875rem', lg: '4.375rem' }} lineHeight="111%" textAlign="center">
        It's time for a new paradigm in cancer: **Prevention**
      </MarkdownText>
      <Flex
        flexDirection={{ base: 'column', lg: 'row' }}
        justifyContent="space-between"
        width={{ base: '100%', lg: '900px' }}
        alignItems="center"
        gap="2rem"
      >
        <Image
          display={{ base: 'none', md: 'block' }}
          ml={{ base: '16px', lg: '0px' }}
          height="325px"
          src="/images/info-images/cancer-cause-misconceptions.png"
          alt="Misconceptions about cancer causes graph"
        />
        <Image
          mt={{ base: '0px', lg: '27px' }}
          height="272px"
          src="/images/info-images/cancer-causes.svg"
          alt="Cancer causes graph"
        />
      </Flex>
    </Section>
  );
}

function SocialProofsSection() {
  return (
    <Section display="flex" flexDirection="column" gap="3rem" alignItems="center" width="100%">
      <MarkdownText
        fontSize={{ base: '1.875rem', lg: '4.375rem' }}
        lineHeight="111%"
        textAlign="center"
        width="90%"
        maxWidth="1000px"
      >
        Join thousands of members **reducing their cancer risk**
      </MarkdownText>
      <SocialProofMarquee />
    </Section>
  );
}

function FAQSection() {
  return (
    <Section display="flex" flexDirection="column" gap="2rem" alignItems="center" width="90%">
      <Text fontSize={{ base: '2.23463rem', md: '4.23463rem' }} lineHeight="111%" maxW="800px" textAlign="center">
        Frequently Asked Questions
      </Text>
      <FAQ variant="dark" />
    </Section>
  );
}

const ctaButtonStyle: ButtonProps = {
  display: 'flex',
  justifyContent: 'center',
  borderRadius: 'radii-button',
  background: 'salmon-light',
  color: 'dark-blue',
  textTransform: 'capitalize',
  fontSize: { base: '1.25rem', lg: '1.25rem' },
  lineHeight: '130%',
  fontWeight: 700,
  paddingX: '2.5rem',
  paddingY: '0.75rem',
  _hover: {
    background: '#ff794f',
  },
  width: '20rem',
};

interface QuizletSellScreenProps {
  results?: QuizletResultsDto;
}

export default function QuizletSellScreen({ results }: QuizletSellScreenProps) {
  const { showStickyButton, mainButtonRef } = useStickyButton();
  const ctaLink = ROUTE_PAYMENT;
  const ctaText = 'Get your risk scores';

  return (
    <Flex
      backgroundColor="white"
      width="100%"
      height="100%"
      flexDirection="column"
      gap={{ base: '50px', md: '100px', lg: '200px' }}
      alignItems="center"
      pb="8rem"
    >
      <Flex flexDirection="column" gap={{ base: '50px', md: '100px' }} width="100%" alignItems="center">
        <HeroWomanHeadlineSection firstName={results?.firstName} />
        <PersonalizedCTASection
          mainButtonRef={mainButtonRef}
          ctaText={ctaText}
          ctaButtonStyle={ctaButtonStyle}
          results={results}
        />
        <DemoAndIndustryProofs />
        <HowItWorksSection />
        <LottieRiskHeadlineSection />
      </Flex>
      <CausesInfographicsSection />
      <SocialProofsSection />
      <FAQSection />
      {showStickyButton && <StickyButton ctaText={ctaText} ctaLink={ctaLink} ctaButtonStyle={ctaButtonStyle} />}
    </Flex>
  );
}
